import React, { useCallback, useEffect, useId, useRef, useState } from 'react'
import { ReactComponent as ArrowsUpDownIcon } from '@assets/images/Input/arrows-up-down.svg'
import { ReactComponent as CalendarIcon } from '@assets/images/Input/calendar.svg'
import { ReactComponent as EmailIcon } from '@assets/images/Input/email.svg'
import { ReactComponent as InfoTooltipIcon } from '@assets/images/Input/info-tooltip.svg'
import { ReactComponent as LocationIcon } from '@assets/images/Input/location.svg'
import { ReactComponent as EyeClosedIcon } from '@assets/images/Input/eye-closed.svg'
import { ReactComponent as EyeOpenIcon } from '@assets/images/Input/eye-open.svg'
import { IconX } from '@tabler/icons-react'
import Badge from '../Badge/Badge.component'
import Calendar from '../Calendar/Calendar.component'
import TimePicker from '../TimePicker/TimePicker.component'
import {
  INPUT_FIELD_ADDRESS_DEMO_SEGMENTS,
  INPUT_FIELD_DEMO_TAGS,
  INPUT_FIELD_DEMO_VALUES,
  INPUT_FIELD_PASSWORD_DEMO_DOTS_COUNT,
  INPUT_FIELD_PASSWORD_DEMO_VISIBLE,
  INPUT_FIELD_PLACEHOLDERS,
  INPUT_FIELD_TAG_COLORS,
  INPUT_FIELD_VARIANT_LABELS,
  type InputFieldProps,
  type InputFieldTag,
} from './InputField.types'
import { detectCardBrandFromNumber, getCardLogo } from '@utils/cardBrand.util'
import type { SavedCard } from '@dataTypes/payment.types'
import {
  formatInputFieldAmount,
  formatInputFieldCardNumber,
  formatInputFieldDate,
  formatInputFieldPhone,
  formatInputFieldTime,
  getInputFieldDateParts,
  getInputFieldTimeParts,
  INPUT_FIELD_CARD_NUMBER_FORMATTED_MAX_LENGTH,
  INPUT_FIELD_US_PHONE_FORMATTED_MAX_LENGTH,
  INPUT_FIELD_EMAIL_INVALID_MESSAGE,
  INPUT_FIELD_VERIFICATION_MAX_DIGITS,
  clampInputFieldNumber,
  isValidInputFieldEmail,
  normalizeInputFieldEmail,
  normalizeInputFieldNumberDigits,
  normalizeInputFieldVerificationDigits,
  parseInputFieldNumber,
  parseInputFieldDate,
  parseInputFieldTime,
} from './InputField.utils'
import './InputField.css'

type AriaProps = {
  'aria-describedby'?: string
  'aria-invalid'?: boolean
  'aria-required'?: boolean
}

const CardBrandIcon: React.FC<{ brand?: SavedCard['brand'] }> = ({
  brand = 'unknown',
}) => (
  <span className="ds-input-field__card-brand" aria-hidden="true">
    {brand === 'unknown' ? (
      <svg width="21" height="13" viewBox="0 0 21 13" fill="none">
        <circle cx="7.5" cy="6.5" r="6.5" fill="#EB001B" />
        <circle cx="13.5" cy="6.5" r="6.5" fill="#F79E1B" fillOpacity="0.85" />
      </svg>
    ) : (
      <img src={getCardLogo(brand)} alt="" width={21} height={13} />
    )}
  </span>
)

const DateValueDisplay: React.FC<
  {
    date: Date
    inputId: string
    isPreview: boolean
    onOpen?: () => void
  } & AriaProps
> = ({ date, inputId, isPreview, onOpen, ...ariaProps }) => {
  const { month, day, year } = getInputFieldDateParts(date)

  return (
    <div
      id={inputId}
      className="ds-input-field__date-value"
      role={isPreview ? undefined : 'button'}
      tabIndex={isPreview ? -1 : 0}
      aria-label={formatInputFieldDate(date)}
      {...ariaProps}
      onClick={isPreview ? undefined : onOpen}
      onKeyDown={
        isPreview
          ? undefined
          : (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                onOpen?.()
              }
            }
      }
    >
      <span className="ds-input-field__date-part">{month}</span>
      <span className="ds-input-field__date-separator" aria-hidden="true">
        /
      </span>
      <span className="ds-input-field__date-part">{day}</span>
      <span className="ds-input-field__date-separator" aria-hidden="true">
        /
      </span>
      <span className="ds-input-field__date-part">{year}</span>
    </div>
  )
}

const TimeValueDisplay: React.FC<
  {
    date: Date
    inputId: string
    isPreview: boolean
    onOpen?: () => void
  } & AriaProps
> = ({ date, inputId, isPreview, onOpen, ...ariaProps }) => {
  const { hours, minutes, period } = getInputFieldTimeParts(date)

  return (
    <div
      id={inputId}
      className="ds-input-field__time-value"
      role={isPreview ? undefined : 'button'}
      tabIndex={isPreview ? -1 : 0}
      aria-label={formatInputFieldTime(date)}
      {...ariaProps}
      onClick={isPreview ? undefined : onOpen}
      onKeyDown={
        isPreview
          ? undefined
          : (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                onOpen?.()
              }
            }
      }
    >
      <span className="ds-input-field__time-part">{hours}</span>
      <span className="ds-input-field__time-separator" aria-hidden="true">
        :
      </span>
      <span className="ds-input-field__time-part">{minutes}</span>
      <span className="ds-input-field__time-period">{period}</span>
    </div>
  )
}

const CardNumberGroupsDisplay: React.FC<
  {
    value: string
    inputId: string
  } & AriaProps
> = ({ value, inputId, ...ariaProps }) => {
  const groups = formatInputFieldCardNumber(value).split(' ').filter(Boolean)

  return (
    <div
      id={inputId}
      className="ds-input-field__card-number-value"
      aria-label={formatInputFieldCardNumber(value)}
      {...ariaProps}
    >
      {groups.map((group, index) => (
        <span key={index} className="ds-input-field__card-group">
          {group}
        </span>
      ))}
    </div>
  )
}

const AddressSegmentsDisplay: React.FC<
  {
    segments: readonly string[]
    inputId: string
  } & AriaProps
> = ({ segments, inputId, ...ariaProps }) => (
  <div
    id={inputId}
    className="ds-input-field__address-value"
    aria-label={segments.join(' ')}
    {...ariaProps}
  >
    {segments.map((segment, index) => (
      <span key={index} className="ds-input-field__address-segment">
        {segment}
      </span>
    ))}
  </div>
)

const InputField: React.FC<InputFieldProps> = ({
  label,
  variant = 'email',
  previewState,
  placeholder,
  value,
  defaultValue,
  helperText,
  optional = false,
  showTooltip = false,
  tooltipLabel = 'More information',
  clearable = false,
  onClear,
  actionLabel = 'Send code',
  onAction,
  tags: tagsProp,
  onTagsChange,
  numberStep = 1,
  numberMin,
  numberMax,
  otpLength = 6,
  maxLength = 100,
  showCounter: showCounterProp,
  leadingIcon,
  trailingIcon,
  darkMode = false,
  disabled,
  fullWidth = false,
  className = '',
  id,
  onChange,
  onBlur,
  onDateChange,
  onTimeChange,
  isDateDisabled,
  rows = 3,
  ...rest
}) => {
  const generatedId = useId()
  const inputId = id ?? generatedId
  const helperId = `${inputId}-helper`
  const pickerFieldRef = useRef<HTMLDivElement>(null)
  const [passwordVisible, setPasswordVisible] = useState(false)
  const [datePickerOpen, setDatePickerOpen] = useState(false)
  const [timePickerOpen, setTimePickerOpen] = useState(false)
  const [internalDateValue, setInternalDateValue] = useState(
    () => defaultValue?.toString() ?? '',
  )
  const [internalCardValue, setInternalCardValue] = useState(
    () => defaultValue?.toString() ?? '',
  )
  const [internalPhoneValue, setInternalPhoneValue] = useState(
    () => defaultValue?.toString() ?? '',
  )
  const [internalAmountValue, setInternalAmountValue] = useState(
    () => defaultValue?.toString() ?? '',
  )
  const [internalVerificationValue, setInternalVerificationValue] = useState(
    () => defaultValue?.toString() ?? '',
  )
  const [internalEmailValue, setInternalEmailValue] = useState(
    () => defaultValue?.toString() ?? '',
  )
  const [internalNumberValue, setInternalNumberValue] = useState(
    () => defaultValue?.toString() ?? '',
  )
  const [emailTouched, setEmailTouched] = useState(false)
  const [internalTags, setInternalTags] = useState<InputFieldTag[]>([])
  const [tagInputValue, setTagInputValue] = useState('')
  const [internalTimeValue, setInternalTimeValue] = useState(
    () => defaultValue?.toString() ?? '',
  )
  const [errorPulse, setErrorPulse] = useState(false)
  const wasFieldErrorRef = useRef(false)
  const isFirstErrorEffectRef = useRef(true)
  const isPreview = previewState != null

  const resolvedDisabled = disabled || previewState === 'disabled'
  const isError = previewState === 'error'
  const isInfo = previewState === 'info'
  const isFocused = previewState === 'focused'
  const isFilled =
    previewState === 'filled' ||
    previewState === 'error' ||
    previewState === 'info' ||
    previewState === 'optional' ||
    previewState === 'tooltip'
  const isOptional = optional || previewState === 'optional'
  const hasTooltip = showTooltip || previewState === 'tooltip'

  const resolvedLabel = label ?? INPUT_FIELD_VARIANT_LABELS[variant]
  const resolvedPlaceholder = placeholder ?? INPUT_FIELD_PLACEHOLDERS[variant]
  const demoValue = INPUT_FIELD_DEMO_VALUES[variant]

  const showAction = variant === 'verification'
  const isDate = variant === 'date'
  const isCardNumber = variant === 'card-number'
  const isPhone = variant === 'phone'
  const isAmount = variant === 'amount'
  const isVerification = variant === 'verification'
  const isNumber = variant === 'number'
  const isEmail = variant === 'email'
  const isAddress = variant === 'address'
  const isTime = variant === 'time'
  const isTextarea = variant === 'message'
  const isOtp = variant === 'verification-v2'
  const isTags = variant === 'tags'
  const showCounter = showCounterProp ?? variant === 'message'

  const displayValue =
    isFilled && !value && !defaultValue ? demoValue : undefined

  const resolvedDateValue =
    value !== undefined ? String(value) : internalDateValue

  const resolvedCardValue =
    value !== undefined ? String(value) : internalCardValue

  const resolvedPhoneValue =
    value !== undefined ? String(value) : internalPhoneValue

  const resolvedAmountValue =
    value !== undefined ? String(value) : internalAmountValue

  const resolvedVerificationValue =
    value !== undefined ? String(value) : internalVerificationValue

  const resolvedEmailValue =
    value !== undefined ? String(value) : internalEmailValue

  const resolvedNumberValue =
    value !== undefined ? String(value) : internalNumberValue

  const resolvedTags = tagsProp ?? internalTags
  const previewTags = isFilled ? (tagsProp ?? INPUT_FIELD_DEMO_TAGS) : []

  const hasEmailValidationError =
    isEmail &&
    !isPreview &&
    emailTouched &&
    resolvedEmailValue.length > 0 &&
    !isValidInputFieldEmail(resolvedEmailValue)

  const showFieldError = isError || hasEmailValidationError

  useEffect(() => {
    if (isFirstErrorEffectRef.current) {
      isFirstErrorEffectRef.current = false
      wasFieldErrorRef.current = showFieldError
      return
    }

    if (showFieldError && !wasFieldErrorRef.current) {
      setErrorPulse(true)
    }

    wasFieldErrorRef.current = showFieldError
  }, [showFieldError])

  const resolvedHelperText =
    helperText ??
    (hasEmailValidationError
      ? INPUT_FIELD_EMAIL_INVALID_MESSAGE
      : isError
        ? 'Error description'
        : isInfo
          ? 'Info text'
          : undefined)

  const ariaProps: AriaProps = {
    'aria-describedby': resolvedHelperText ? helperId : undefined,
    'aria-invalid': showFieldError ? true : undefined,
    'aria-required': rest.required ? true : undefined,
  }

  const resolvedTimeValue =
    value !== undefined ? String(value) : internalTimeValue

  const inputValue =
    isDate && !isPreview
      ? resolvedDateValue
      : isCardNumber && !isPreview
        ? resolvedCardValue
        : isPhone && !isPreview
          ? resolvedPhoneValue
          : isAmount && !isPreview
            ? resolvedAmountValue
            : isVerification && !isPreview
              ? resolvedVerificationValue
              : isNumber && !isPreview
                ? resolvedNumberValue
                : isEmail && !isPreview
                  ? resolvedEmailValue
                  : isTime && !isPreview
                    ? resolvedTimeValue
                    : (value ?? displayValue ?? '')

  const charCount =
    typeof inputValue === 'string' ? inputValue.length : demoValue.length

  const hasInteractiveValue =
    !isPreview &&
    (isTags
      ? resolvedTags.length > 0 || tagInputValue.length > 0
      : String(inputValue).length > 0)
  const showDateChevron = isDate && !hasInteractiveValue && !isFilled
  const showTimeChevron = isTime && !hasInteractiveValue && !isFilled

  const showClear =
    clearable ||
    hasInteractiveValue ||
    (isFilled &&
      variant !== 'verification' &&
      variant !== 'verification-v2' &&
      variant !== 'message')

  const selectedDate =
    isDate && !isPreview
      ? parseInputFieldDate(resolvedDateValue)
      : isDate && isFilled
        ? parseInputFieldDate(demoValue)
        : null

  const selectedTime =
    isTime && !isPreview
      ? parseInputFieldTime(resolvedTimeValue)
      : isTime && isFilled
        ? parseInputFieldTime(demoValue)
        : null

  const openDatePicker = useCallback((): void => {
    if (!isDate || isPreview || resolvedDisabled) {
      return
    }

    setDatePickerOpen(true)
  }, [isDate, isPreview, resolvedDisabled])

  const closeDatePicker = useCallback((): void => {
    setDatePickerOpen(false)
  }, [])

  const openTimePicker = useCallback((): void => {
    if (!isTime || isPreview || resolvedDisabled) {
      return
    }

    setTimePickerOpen(true)
  }, [isTime, isPreview, resolvedDisabled])

  const closeTimePicker = useCallback((): void => {
    setTimePickerOpen(false)
  }, [])

  const emitDateValue = useCallback(
    (nextValue: string, date: Date | null): void => {
      if (value === undefined) {
        setInternalDateValue(nextValue)
      }

      if (date) {
        onDateChange?.(date)
      }

      onChange?.({
        target: { value: nextValue },
      } as React.ChangeEvent<HTMLInputElement>)
    },
    [onChange, onDateChange, value],
  )

  const handleDateSelect = useCallback(
    (date: Date): void => {
      emitDateValue(formatInputFieldDate(date), date)
      closeDatePicker()
    },
    [closeDatePicker, emitDateValue],
  )

  const handleDateClear = useCallback((): void => {
    emitDateValue('', null)
    closeDatePicker()
    onClear?.()
  }, [closeDatePicker, emitDateValue, onClear])

  const emitTimeValue = useCallback(
    (nextValue: string, time: Date | null): void => {
      if (value === undefined) {
        setInternalTimeValue(nextValue)
      }

      if (time) {
        onTimeChange?.(time)
      }

      onChange?.({
        target: { value: nextValue },
      } as React.ChangeEvent<HTMLInputElement>)
    },
    [onChange, onTimeChange, value],
  )

  const handleTimeSelect = useCallback(
    (time: Date): void => {
      emitTimeValue(formatInputFieldTime(time), time)
    },
    [emitTimeValue],
  )

  const handleTimeClear = useCallback((): void => {
    emitTimeValue('', null)
    closeTimePicker()
    onClear?.()
  }, [closeTimePicker, emitTimeValue, onClear])

  const handleCardNumberChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const formatted = formatInputFieldCardNumber(event.target.value)

      if (value === undefined) {
        setInternalCardValue(formatted)
      }

      onChange?.({
        ...event,
        target: { ...event.target, value: formatted },
      })
    },
    [onChange, value],
  )

  const handleCardNumberClear = useCallback((): void => {
    if (value === undefined) {
      setInternalCardValue('')
    }

    onChange?.({
      target: { value: '' },
    } as React.ChangeEvent<HTMLInputElement>)
    onClear?.()
  }, [onChange, onClear, value])

  const handlePhoneChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const formatted = formatInputFieldPhone(event.target.value)

      if (value === undefined) {
        setInternalPhoneValue(formatted)
      }

      onChange?.({
        ...event,
        target: { ...event.target, value: formatted },
      })
    },
    [onChange, value],
  )

  const handlePhoneClear = useCallback((): void => {
    if (value === undefined) {
      setInternalPhoneValue('')
    }

    onChange?.({
      target: { value: '' },
    } as React.ChangeEvent<HTMLInputElement>)
    onClear?.()
  }, [onChange, onClear, value])

  const handleAmountChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const formatted = formatInputFieldAmount(event.target.value)

      if (value === undefined) {
        setInternalAmountValue(formatted)
      }

      onChange?.({
        ...event,
        target: { ...event.target, value: formatted },
      })
    },
    [onChange, value],
  )

  const handleAmountClear = useCallback((): void => {
    if (value === undefined) {
      setInternalAmountValue('')
    }

    onChange?.({
      target: { value: '' },
    } as React.ChangeEvent<HTMLInputElement>)
    onClear?.()
  }, [onChange, onClear, value])

  const handleVerificationChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const digits = normalizeInputFieldVerificationDigits(event.target.value)

      if (value === undefined) {
        setInternalVerificationValue(digits)
      }

      onChange?.({
        ...event,
        target: { ...event.target, value: digits },
      })
    },
    [onChange, value],
  )

  const handleVerificationClear = useCallback((): void => {
    if (value === undefined) {
      setInternalVerificationValue('')
    }

    onChange?.({
      target: { value: '' },
    } as React.ChangeEvent<HTMLInputElement>)
    onClear?.()
  }, [onChange, onClear, value])

  const handleEmailChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const normalized = normalizeInputFieldEmail(event.target.value)

      if (value === undefined) {
        setInternalEmailValue(normalized)
      }

      onChange?.({
        ...event,
        target: { ...event.target, value: normalized },
      })
    },
    [onChange, value],
  )

  const handleEmailBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>): void => {
      setEmailTouched(true)
      onBlur?.(event)
    },
    [onBlur],
  )

  const handleEmailClear = useCallback((): void => {
    if (value === undefined) {
      setInternalEmailValue('')
    }

    setEmailTouched(false)

    onChange?.({
      target: { value: '' },
    } as React.ChangeEvent<HTMLInputElement>)
    onClear?.()
  }, [onChange, onClear, value])

  const emitNumberValue = useCallback(
    (nextValue: string): void => {
      if (value === undefined) {
        setInternalNumberValue(nextValue)
      }

      onChange?.({
        target: { value: nextValue },
      } as React.ChangeEvent<HTMLInputElement>)
    },
    [onChange, value],
  )

  const handleNumberChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const normalized = normalizeInputFieldNumberDigits(event.target.value)
      emitNumberValue(normalized)
    },
    [emitNumberValue],
  )

  const adjustNumberValue = useCallback(
    (delta: number): void => {
      const currentValue = parseInputFieldNumber(resolvedNumberValue) ?? 0
      const nextValue = clampInputFieldNumber(
        currentValue + delta,
        numberMin,
        numberMax,
      )

      emitNumberValue(String(nextValue))
    },
    [emitNumberValue, numberMax, numberMin, resolvedNumberValue],
  )

  const handleNumberIncrement = useCallback((): void => {
    adjustNumberValue(numberStep)
  }, [adjustNumberValue, numberStep])

  const handleNumberDecrement = useCallback((): void => {
    adjustNumberValue(-numberStep)
  }, [adjustNumberValue, numberStep])

  const handleNumberClear = useCallback((): void => {
    emitNumberValue('')
    onClear?.()
  }, [emitNumberValue, onClear])

  const emitTagsChange = useCallback(
    (nextTags: InputFieldTag[]): void => {
      if (tagsProp === undefined) {
        setInternalTags(nextTags)
      }

      onTagsChange?.(nextTags)
    },
    [onTagsChange, tagsProp],
  )

  const commitTagInput = useCallback((): void => {
    const nextTagLabel = tagInputValue.trim().replace(/,$/, '')

    if (!nextTagLabel || resolvedDisabled) {
      return
    }

    const isDuplicate = resolvedTags.some(
      (tag) => tag.label.toLowerCase() === nextTagLabel.toLowerCase(),
    )

    if (isDuplicate) {
      setTagInputValue('')
      return
    }

    emitTagsChange([
      ...resolvedTags,
      {
        label: nextTagLabel,
        color:
          INPUT_FIELD_TAG_COLORS[
            resolvedTags.length % INPUT_FIELD_TAG_COLORS.length
          ],
      },
    ])
    setTagInputValue('')
  }, [emitTagsChange, resolvedDisabled, resolvedTags, tagInputValue])

  const handleTagInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      setTagInputValue(event.target.value)
    },
    [],
  )

  const handleTagsKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>): void => {
      if (event.key === 'Enter' || event.key === ',') {
        event.preventDefault()
        commitTagInput()
        return
      }

      if (
        event.key === 'Backspace' &&
        tagInputValue.length === 0 &&
        resolvedTags.length > 0
      ) {
        emitTagsChange(resolvedTags.slice(0, -1))
      }
    },
    [commitTagInput, emitTagsChange, resolvedTags, tagInputValue],
  )

  const removeTag = useCallback(
    (index: number): void => {
      emitTagsChange(resolvedTags.filter((_, tagIndex) => tagIndex !== index))
    },
    [emitTagsChange, resolvedTags],
  )

  const handleTagsClear = useCallback((): void => {
    setTagInputValue('')

    if (tagsProp === undefined) {
      setInternalTags([])
    }

    onTagsChange?.([])
    onClear?.()
  }, [onClear, onTagsChange, tagsProp])

  useEffect(() => {
    if (isPreview || (!datePickerOpen && !timePickerOpen)) {
      return undefined
    }

    const handlePointerDown = (event: MouseEvent): void => {
      if (!pickerFieldRef.current?.contains(event.target as Node)) {
        closeDatePicker()
        closeTimePicker()
      }
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        closeDatePicker()
        closeTimePicker()
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [
    closeDatePicker,
    closeTimePicker,
    datePickerOpen,
    isPreview,
    timePickerOpen,
  ])

  const hasPasswordContent =
    variant === 'password' &&
    (isPreview
      ? previewState === 'disabled' || isFilled
      : String(inputValue).length > 0)

  const showPasswordToggle =
    variant === 'password' && !resolvedDisabled && hasPasswordContent

  const classNames = [
    'ds-input-field',
    `ds-input-field--${variant}`,
    fullWidth ? 'ds-input-field--full-width' : '',
    darkMode ? 'ds-input-field--dark' : '',
    isPreview ? 'ds-input-field--preview' : '',
    previewState ? `ds-input-field--preview-${previewState}` : '',
    isFocused ? 'ds-input-field--focused' : '',
    showFieldError ? 'ds-input-field--error' : '',
    errorPulse ? 'ds-input-field--error-pulse' : '',
    resolvedDisabled ? 'ds-input-field--disabled' : '',
    isDate && datePickerOpen ? 'ds-input-field--date-open' : '',
    isTime && timePickerOpen ? 'ds-input-field--time-open' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const renderLeading = (): React.ReactNode => {
    if (leadingIcon) {
      return (
        <span className="ds-input-field__leading-icon" aria-hidden="true">
          {leadingIcon}
        </span>
      )
    }

    switch (variant) {
      case 'email':
        return (
          <span className="ds-input-field__leading-icon" aria-hidden="true">
            <EmailIcon />
          </span>
        )
      case 'date':
        if (isPreview) {
          return (
            <span className="ds-input-field__leading-icon" aria-hidden="true">
              <CalendarIcon />
            </span>
          )
        }

        return (
          <button
            type="button"
            className="ds-input-field__date-trigger ds-input-field__date-trigger--leading"
            aria-label="Open calendar"
            disabled={resolvedDisabled}
            onClick={openDatePicker}
          >
            <CalendarIcon />
          </button>
        )
      case 'time':
        if (isPreview) {
          return (
            <span
              className="mgc mgc_time_line ds-input-field__leading-icon"
              aria-hidden="true"
            />
          )
        }

        return (
          <button
            type="button"
            className="ds-input-field__date-trigger ds-input-field__date-trigger--leading"
            aria-label="Open time picker"
            disabled={resolvedDisabled}
            onClick={openTimePicker}
          >
            <span className="mgc mgc_time_line" aria-hidden="true" />
          </button>
        )
      case 'card-number':
        return (
          <CardBrandIcon
            brand={detectCardBrandFromNumber(
              isPreview && isFilled ? demoValue : resolvedCardValue,
            )}
          />
        )
      case 'number':
        if (isPreview) {
          return (
            <span className="ds-input-field__leading-icon" aria-hidden="true">
              <ArrowsUpDownIcon />
            </span>
          )
        }

        return (
          <span className="ds-input-field__number-stepper">
            <ArrowsUpDownIcon
              className="ds-input-field__number-stepper-icon"
              aria-hidden="true"
            />
            <button
              type="button"
              className="ds-input-field__number-stepper-btn ds-input-field__number-stepper-btn--up"
              aria-label="Increase value"
              disabled={resolvedDisabled}
              onClick={handleNumberIncrement}
              tabIndex={0}
            />
            <button
              type="button"
              className="ds-input-field__number-stepper-btn ds-input-field__number-stepper-btn--down"
              aria-label="Decrease value"
              disabled={resolvedDisabled}
              onClick={handleNumberDecrement}
              tabIndex={0}
            />
          </span>
        )
      case 'address':
        return (
          <span className="ds-input-field__leading-icon" aria-hidden="true">
            <LocationIcon />
          </span>
        )
      case 'phone':
        return (
          <span className="ds-input-field__prefix" aria-hidden="true">
            +1
          </span>
        )
      case 'amount':
        return (
          <span className="ds-input-field__prefix" aria-hidden="true">
            $
          </span>
        )
      case 'password':
        if (!showPasswordToggle) {
          return null
        }

        if (isPreview) {
          return (
            <span className="ds-input-field__leading-icon" aria-hidden="true">
              {passwordVisible ? <EyeClosedIcon /> : <EyeOpenIcon />}
            </span>
          )
        }

        return (
          <button
            type="button"
            className="ds-input-field__password-toggle"
            aria-label={passwordVisible ? 'Hide password' : 'Show password'}
            onClick={() => setPasswordVisible((visible) => !visible)}
          >
            {passwordVisible ? <EyeClosedIcon /> : <EyeOpenIcon />}
          </button>
        )
      default:
        return null
    }
  }

  const renderTrailing = (): React.ReactNode => {
    if (trailingIcon) {
      return (
        <span className="ds-input-field__trailing-icon" aria-hidden="true">
          {trailingIcon}
        </span>
      )
    }

    if (showAction) {
      return (
        <button
          type="button"
          className="ds-input-field__action"
          disabled={resolvedDisabled}
          onClick={onAction}
          tabIndex={isPreview ? -1 : 0}
        >
          {actionLabel}
        </button>
      )
    }

    if (showDateChevron || showTimeChevron) {
      return (
        <button
          type="button"
          className="ds-input-field__date-trigger"
          aria-label={isDate ? 'Open calendar' : 'Open time picker'}
          disabled={resolvedDisabled}
          onClick={
            isDate ? openDatePicker : isTime ? openTimePicker : undefined
          }
          tabIndex={isPreview ? -1 : 0}
        >
          <i className="mgc mgc_down_small_fill" aria-hidden="true" />
        </button>
      )
    }

    if (showClear) {
      return (
        <button
          type="button"
          className="ds-input-field__clear"
          aria-label="Clear input"
          disabled={resolvedDisabled}
          onClick={
            isDate
              ? handleDateClear
              : isTime
                ? handleTimeClear
                : isCardNumber
                  ? handleCardNumberClear
                  : isPhone
                    ? handlePhoneClear
                    : isAmount
                      ? handleAmountClear
                      : isVerification
                        ? handleVerificationClear
                        : isEmail
                          ? handleEmailClear
                          : isNumber
                            ? handleNumberClear
                            : isTags
                              ? handleTagsClear
                              : onClear
          }
          tabIndex={isPreview ? -1 : 0}
        >
          <IconX size={16} stroke={2} />
        </button>
      )
    }

    return null
  }

  const renderFieldBody = (): React.ReactNode => {
    if (isOtp) {
      const otpDigits = isFilled ? '045789'.slice(0, otpLength) : ''
      return (
        <div
          className="ds-input-field__otp"
          role="group"
          aria-label={resolvedLabel}
          {...ariaProps}
        >
          {Array.from({ length: otpLength }, (_, index) => (
            <div
              key={index}
              className={`ds-input-field__otp-cell${
                otpDigits[index] ? ' ds-input-field__otp-cell--filled' : ''
              }${
                isFocused && index === 0
                  ? ' ds-input-field__otp-cell--focused'
                  : ''
              }`}
            >
              {otpDigits[index] ?? ''}
            </div>
          ))}
        </div>
      )
    }

    if (isTags) {
      if (isPreview) {
        return (
          <div className="ds-input-field__tags">
            {previewTags.map((tag) => (
              <Badge
                key={tag.label}
                label={tag.label}
                mode="tinted"
                color={tag.color}
              />
            ))}
          </div>
        )
      }

      return (
        <div className="ds-input-field__tags ds-input-field__tags--interactive">
          {resolvedTags.map((tag, index) => (
            <Badge
              key={`${tag.label}-${index}`}
              label={tag.label}
              mode="tinted"
              color={tag.color}
              dismissible={!resolvedDisabled}
              onDismiss={() => removeTag(index)}
            />
          ))}
          {!resolvedDisabled ? (
            <input
              id={inputId}
              type="text"
              className="ds-input-field__tags-input"
              value={tagInputValue}
              placeholder={
                resolvedTags.length === 0 ? resolvedPlaceholder : undefined
              }
              disabled={resolvedDisabled}
              onChange={handleTagInputChange}
              onKeyDown={handleTagsKeyDown}
              onBlur={commitTagInput}
              aria-label={resolvedLabel}
              {...ariaProps}
            />
          ) : null}
        </div>
      )
    }

    if (isTextarea) {
      return (
        <div className="ds-input-field__textarea-wrap">
          <textarea
            id={inputId}
            className="ds-input-field__textarea"
            placeholder={resolvedPlaceholder}
            disabled={resolvedDisabled}
            maxLength={maxLength}
            rows={rows}
            value={value}
            defaultValue={defaultValue ?? (isFilled ? demoValue : undefined)}
            onChange={onChange}
            readOnly={isPreview}
            data-preview-state={previewState}
            {...(rest as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
            {...ariaProps}
          />
          {showCounter ? (
            <span className="ds-input-field__counter" aria-live="polite">
              {charCount}/{maxLength}
            </span>
          ) : null}
        </div>
      )
    }

    if (variant === 'password' && isPreview && previewState === 'disabled') {
      return (
        <div className="ds-input-field__password-mask" aria-hidden="true">
          {Array.from({ length: INPUT_FIELD_PASSWORD_DEMO_DOTS_COUNT }).map(
            (_, index) => (
              <span key={index} className="ds-input-field__password-dot">
                •
              </span>
            ),
          )}
        </div>
      )
    }

    if (variant === 'password' && isPreview && isFilled) {
      return (
        <div className="ds-input-field__password-display" aria-hidden="true">
          <span className="ds-input-field__password-visible">
            {INPUT_FIELD_PASSWORD_DEMO_VISIBLE}
          </span>
          <span className="ds-input-field__password-dots">
            {Array.from({ length: INPUT_FIELD_PASSWORD_DEMO_DOTS_COUNT }).map(
              (_, index) => (
                <span key={index} className="ds-input-field__password-dot">
                  •
                </span>
              ),
            )}
          </span>
        </div>
      )
    }

    if (isAddress && isPreview && isFilled) {
      return (
        <AddressSegmentsDisplay
          inputId={inputId}
          segments={INPUT_FIELD_ADDRESS_DEMO_SEGMENTS}
          {...ariaProps}
        />
      )
    }

    if (isCardNumber && isPreview && isFilled) {
      return (
        <CardNumberGroupsDisplay
          inputId={inputId}
          value={demoValue}
          {...ariaProps}
        />
      )
    }

    if (isCardNumber && !isPreview) {
      return (
        <input
          id={inputId}
          type="text"
          className="ds-input-field__input"
          placeholder={resolvedPlaceholder}
          disabled={resolvedDisabled}
          value={formatInputFieldCardNumber(resolvedCardValue)}
          inputMode="numeric"
          autoComplete="cc-number"
          maxLength={INPUT_FIELD_CARD_NUMBER_FORMATTED_MAX_LENGTH}
          onChange={handleCardNumberChange}
          data-preview-state={previewState}
          {...rest}
          {...ariaProps}
        />
      )
    }

    if (isPhone && !isPreview) {
      return (
        <input
          id={inputId}
          type="tel"
          className="ds-input-field__input"
          placeholder={resolvedPlaceholder}
          disabled={resolvedDisabled}
          value={formatInputFieldPhone(resolvedPhoneValue)}
          inputMode="tel"
          autoComplete="tel-national"
          maxLength={INPUT_FIELD_US_PHONE_FORMATTED_MAX_LENGTH}
          onChange={handlePhoneChange}
          data-preview-state={previewState}
          {...rest}
          {...ariaProps}
        />
      )
    }

    if (isAmount && !isPreview) {
      return (
        <input
          id={inputId}
          type="text"
          className="ds-input-field__input"
          placeholder={resolvedPlaceholder}
          disabled={resolvedDisabled}
          value={formatInputFieldAmount(resolvedAmountValue)}
          inputMode="decimal"
          autoComplete="off"
          onChange={handleAmountChange}
          data-preview-state={previewState}
          {...rest}
          {...ariaProps}
        />
      )
    }

    if (isVerification && !isPreview) {
      return (
        <input
          id={inputId}
          type="text"
          className="ds-input-field__input"
          placeholder={resolvedPlaceholder}
          disabled={resolvedDisabled}
          value={resolvedVerificationValue}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={INPUT_FIELD_VERIFICATION_MAX_DIGITS}
          onChange={handleVerificationChange}
          data-preview-state={previewState}
          {...rest}
          {...ariaProps}
        />
      )
    }

    if (isEmail && !isPreview) {
      return (
        <input
          id={inputId}
          type="email"
          className="ds-input-field__input"
          placeholder={resolvedPlaceholder}
          disabled={resolvedDisabled}
          value={resolvedEmailValue}
          inputMode="email"
          autoComplete="email"
          data-preview-state={previewState}
          {...rest}
          {...ariaProps}
          onChange={handleEmailChange}
          onBlur={handleEmailBlur}
        />
      )
    }

    if (isTime && selectedTime) {
      return (
        <TimeValueDisplay
          date={selectedTime}
          inputId={inputId}
          isPreview={isPreview}
          onOpen={openTimePicker}
          {...ariaProps}
        />
      )
    }

    if (isTime && !isPreview && !selectedTime) {
      return (
        <input
          id={inputId}
          type="text"
          className="ds-input-field__input"
          placeholder={resolvedPlaceholder}
          disabled={resolvedDisabled}
          value=""
          readOnly
          onClick={openTimePicker}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              openTimePicker()
            }
          }}
          {...rest}
          {...ariaProps}
        />
      )
    }

    if (isDate && selectedDate) {
      return (
        <DateValueDisplay
          date={selectedDate}
          inputId={inputId}
          isPreview={isPreview}
          onOpen={openDatePicker}
          {...ariaProps}
        />
      )
    }

    if (isDate && !isPreview && !selectedDate) {
      return (
        <input
          id={inputId}
          type="text"
          className="ds-input-field__input"
          placeholder={resolvedPlaceholder}
          disabled={resolvedDisabled}
          value=""
          readOnly
          onClick={openDatePicker}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              openDatePicker()
            }
          }}
          {...rest}
          {...ariaProps}
        />
      )
    }

    if (isNumber && !isPreview) {
      return (
        <input
          id={inputId}
          type="text"
          className="ds-input-field__input"
          placeholder={resolvedPlaceholder}
          disabled={resolvedDisabled}
          value={resolvedNumberValue}
          inputMode="numeric"
          onChange={handleNumberChange}
          data-preview-state={previewState}
          {...rest}
          {...ariaProps}
        />
      )
    }

    const inputType =
      variant === 'password' ? (passwordVisible ? 'text' : 'password') : 'text'

    return (
      <input
        id={inputId}
        type={inputType}
        className="ds-input-field__input"
        placeholder={isFilled ? undefined : resolvedPlaceholder}
        disabled={resolvedDisabled}
        value={value}
        defaultValue={
          defaultValue ??
          (isFilled && value === undefined ? demoValue : undefined)
        }
        onChange={onChange}
        readOnly={isPreview}
        data-preview-state={previewState}
        {...rest}
        {...ariaProps}
      />
    )
  }

  const renderControlShell = (): React.ReactNode => {
    if (isOtp) {
      return renderFieldBody()
    }

    const control = (
      <div className="ds-input-field__control">
        {!isTags ? renderLeading() : null}
        {renderFieldBody()}
        {renderTrailing()}
      </div>
    )

    return control
  }

  return (
    <div
      className={classNames}
      onAnimationEnd={(event) => {
        if (event.target !== event.currentTarget) return
        if (event.animationName === 'ds-input-field-error-shake') {
          setErrorPulse(false)
        }
      }}
    >
      <div className="ds-input-field__label-row">
        <label
          className="ds-input-field__label"
          htmlFor={isOtp ? undefined : inputId}
        >
          {resolvedLabel}
          {isOptional ? (
            <span className="ds-input-field__optional"> (Optional)</span>
          ) : null}
        </label>
        {hasTooltip ? (
          <span
            className="ds-input-field__tooltip-trigger"
            title={tooltipLabel}
            aria-label={tooltipLabel}
          >
            <InfoTooltipIcon />
          </span>
        ) : null}
      </div>

      <div
        className={`ds-input-field__field${
          isDate ? ' ds-input-field__field--date' : ''
        }${isTime ? ' ds-input-field__field--time' : ''}`}
        ref={isDate || isTime ? pickerFieldRef : undefined}
      >
        {renderControlShell()}
        {isDate && !isPreview && datePickerOpen ? (
          <div className="ds-input-field__date-picker">
            <Calendar
              mode="date"
              value={selectedDate}
              defaultViewDate={selectedDate ?? new Date()}
              onChange={handleDateSelect}
              isDateDisabled={isDateDisabled}
              darkMode={darkMode}
            />
          </div>
        ) : null}
        {isTime && !isPreview && timePickerOpen ? (
          <div className="ds-input-field__time-picker">
            <TimePicker
              value={selectedTime}
              onChange={handleTimeSelect}
              darkMode={darkMode}
            />
          </div>
        ) : null}
      </div>

      {resolvedHelperText ? (
        <p
          key={
            showFieldError
              ? `helper-error-${resolvedHelperText}`
              : `helper-${resolvedHelperText}`
          }
          id={helperId}
          className={`ds-input-field__helper${
            showFieldError
              ? ' ds-input-field__helper--error ds-input-field__helper--enter'
              : ''
          }`}
          role={showFieldError ? 'alert' : undefined}
        >
          {resolvedHelperText}
        </p>
      ) : null}
    </div>
  )
}

export default InputField
